var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { AwsUi } from '../../../core/runtime/src';
import LegacyClassBuilder from '../../../core/utils/src/class-builders/legacy';
import { Icon } from '../../icon/src';
import { KeyCode } from '../../../core/utils/src/dom';
import { addEnterAnimation, removeEnterAnimation } from '../../../core/utils/src/motion/helpers';
var lastGeneratedId = 0;
function nextGeneratedId() {
    return 'awsui-expandable-section-' + lastGeneratedId++;
}
export var ExpandableSection = AwsUi.__addComponent('awsui-expandable-section', {
    releaseStatus: 'stable',
    callbacks: {
        initialized: function () {
            this.controlId = nextGeneratedId();
            this.triggerControlId = this.controlId + "-trigger";
        }
    },
    properties: {
        // If true, the component will display the content area.  If false, the component will hide
        // the content area.
        expanded: {
            type: 'boolean'
        },
        // The possible variants of an expandable section are the following
        // * `default`: can be used in any context
        // * `borderless`: supposed to be used in form section footers
        // * `container`: supposed to be used in a detail page along with other containers
        // (please check `awsui-util-container` examples for further detail)
        // * `navigation`: supposed to be used in the navigation panel with anchors and custom styled content,
        // it comes without any styles
        variant: {
            type: 'string',
            valid: ['default', 'borderless', 'container', 'navigation'],
            defaultValue: 'default'
        }
    },
    regions: {
        // Primary content displayed in the expandable section element.
        content: {
            isDefault: true
        },
        // Heading displayed above the content text.
        header: {}
    },
    events: {
        // Fired when the state has changed (it has been expanded or collapsed).
        // The event `detail` contains the current value of the `expanded` property.
        change: {
            bubbles: true,
            cancelable: false,
            detailType: 'ExpandableSection.ChangeDetail'
        }
    },
    render: function (m, h) {
        var _this = this;
        var isContainer = this.variant === 'container';
        var isNavigation = this.variant === 'navigation';
        var applyFocusableAttributes = function (attributes) {
            attributes['aria-controls'] = _this.controlId;
            attributes['aria-expanded'] = _this.expanded.toString();
            attributes['override-focus'] = true;
            return attributes;
        };
        var header = function () {
            var changeHandler = function () {
                _this.expanded = !_this.expanded;
                _this.__fireEvent('change', { expanded: _this.expanded });
                var container = _this.node.querySelector('.awsui-expandable-section-content');
                var motionOpenedClass = 'awsui-motion-entered';
                if (_this.expanded) {
                    addEnterAnimation(container, motionOpenedClass);
                }
                else {
                    removeEnterAnimation(container, motionOpenedClass);
                }
            };
            var clickHandler = function () {
                changeHandler();
            };
            var keyUpHandler = function (event) {
                var interactionKeys = [KeyCode.enter, KeyCode.space];
                if (interactionKeys.indexOf(event.keyCode) !== -1) {
                    changeHandler();
                }
            };
            var keyDownHandler = function (event) {
                if (event.keyCode === KeyCode.space) {
                    // Prevent the page from scrolling when toggling the component with the space bar.
                    event.preventDefault();
                }
            };
            var headerCb = new LegacyClassBuilder(_this)
                .addComponentClass('header')
                .addComponentClass("header-" + _this.variant)
                .addComponentClass('header-expanded', _this.expanded)
                .addClass('awsui-util-container-header', isContainer);
            var iconCb = new LegacyClassBuilder(_this)
                .addComponentClass('header-icon')
                .addComponentClass("header-icon-" + _this.variant);
            var headerAttributes = {
                onclick: clickHandler,
                id: _this.triggerControlId
            };
            var iconAttributes = {};
            // Navigation must contain anchors, so we make only icon focusable
            var IconTag = 'DIV';
            if (isNavigation) {
                applyFocusableAttributes(iconAttributes);
                iconCb.addComponentClass('header-icon-focusable').addComponentClass('trigger');
                IconTag = 'BUTTON';
                iconAttributes.type = 'button';
                iconAttributes['aria-labelledby'] = _this.triggerControlId;
            }
            else {
                applyFocusableAttributes(headerAttributes);
                headerCb.addComponentClass('trigger');
                headerAttributes.role = 'button';
                headerAttributes.tabindex = '0';
                headerAttributes.onkeyup = keyUpHandler;
                headerAttributes.onkeydown = keyDownHandler;
            }
            return (AwsUi.__createElement("div", __assign({}, headerAttributes, { className: headerCb.toClassName() }),
                AwsUi.__createElement(IconTag, __assign({}, iconAttributes, { className: iconCb.toClassName() }),
                    AwsUi.__createElement(Icon, { name: "caret-down-filled" })),
                h.region('header')));
        };
        var content = function () {
            var cb = new LegacyClassBuilder(_this)
                .addComponentClass('content')
                .addComponentClass("content-" + _this.variant)
                .addComponentClass('content-expanded', _this.expanded);
            var attributes = {
                class: cb.toClassName(),
                id: _this.controlId,
                role: 'region',
                'aria-labelledby': _this.triggerControlId
            };
            return h.region('content', 'div', attributes);
        };
        var cb = new LegacyClassBuilder(this)
            .addClass('awsui-expandable-section')
            .addClass('awsui-util-container', isContainer);
        return AwsUi.__createElement("div", { className: cb.toClassName() }, [header(), content()]);
    }
});
//# sourceMappingURL=index.js.map